import { useBilling, ActiveSubscription } from "../hooks/useBilling";

const STATUS_LABEL: Record<ActiveSubscription["status"], string> = {
  ACTIVE: "Activo",
  TRIAL: "Período de prueba",
  PAST_DUE: "Pago pendiente",
  CANCELLED: "Cancelado",
  EXPIRED: "Expirado",
};

const STATUS_TONE: Record<
  ActiveSubscription["status"],
  "success" | "warning" | "critical" | "info"
> = {
  ACTIVE: "success",
  TRIAL: "info",
  PAST_DUE: "warning",
  CANCELLED: "critical",
  EXPIRED: "critical",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BillingPage() {
  const { subscription, pricingUrl, loading } = useBilling();

  if (loading) {
    return (
      <s-page heading="Plan y facturación">
        <s-section>
          <s-spinner accessibility-label="Cargando..." />
        </s-section>
      </s-page>
    );
  }

  return (
    <s-page heading="Plan y facturación">
      {/* ── Active / Trial subscription ── */}
      {subscription ? (
        <s-section heading="Tu plan actual">
          <s-stack direction="block" gap="base">
            <s-stack direction="inline" gap="small-200" align-items="center">
              <s-heading>{subscription.plan.name}</s-heading>
              <s-badge tone={STATUS_TONE[subscription.status]}>
                {STATUS_LABEL[subscription.status]}
              </s-badge>
            </s-stack>

            {subscription.plan.orderLimit != null && (
              <s-text color="subdued">
                Hasta {subscription.plan.orderLimit.toLocaleString("es")}{" "}
                pedidos / mes
              </s-text>
            )}

            {subscription.trialEndsAt &&
              new Date(subscription.trialEndsAt) > new Date() && (
                <s-banner tone="info" heading="Período de prueba activo">
                  Tu prueba gratuita finaliza el{" "}
                  {formatDate(subscription.trialEndsAt)}.
                </s-banner>
              )}

            {subscription.status === "PAST_DUE" && (
              <s-banner tone="warning" heading="Pago pendiente">
                Hay un problema con el pago de tu suscripción. Actualiza tu
                método de pago para continuar usando la aplicación.
              </s-banner>
            )}

            <s-text color="subdued">
              Próxima renovación: {formatDate(subscription.currentPeriodEnd)}
            </s-text>
          </s-stack>
        </s-section>
      ) : (
        /* ── No subscription ── */
        <s-section>
          <s-stack direction="block" gap="base">
            <s-heading>Sin plan activo</s-heading>
            <s-text color="subdued">
              Selecciona un plan para continuar usando todas las funciones de
              Codway.
            </s-text>
          </s-stack>
        </s-section>
      )}

      {/* ── Manage plan CTA ── */}
      {pricingUrl && (
        <s-section heading="Gestionar suscripción">
          <s-stack direction="block" gap="base">
            <s-text color="subdued">
              Los planes son gestionados directamente por Shopify. Al hacer clic
              serás redirigido a la página de planes de tu tienda.
            </s-text>
            <s-button variant="primary" href={pricingUrl} target="_top">
              {subscription ? "Cambiar plan" : "Ver planes"}
            </s-button>
          </s-stack>
        </s-section>
      )}
    </s-page>
  );
}
